import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFetchUserBookings } from './useFetchUserBookings';
import { useFetchBookedStudios } from './useFetchBookedStudios';
import { hasUnviewedStatusChange, getLastViewedStatus, hasNewBooking } from '../util/bookingViewedTracker';

/**
 * Hook to get booking notifications count
 * Shows notifications for bookings with unviewed status changes
 * Only for Creator mode (hosts) - guests don't see booking notifications
 * @param {string|null} mode - 'explorer', 'creator', or null (null = don't fetch)
 * @returns {number} Count of bookings with unviewed status changes
 */
export const useBookingNotifications = (mode = null) => {
  const { user } = useSelector((state) => state?.auth);
  const userId = user?.id || user?._id;
  const location = useLocation();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Don't fetch if mode is null (for guests)
  const shouldFetchExplorer = mode === 'explorer';
  const shouldFetchCreator = mode === 'creator';
  
  // Fetch bookings based on mode
  const { bookingStudioData: explorerBookings, loading: explorerLoading, refetch: refetchExplorer } = useFetchUserBookings(
    shouldFetchExplorer ? userId : null
  );
  
  const { bookingStudioData: creatorBookings, loading: creatorLoading, refetch: refetchCreator } = useFetchBookedStudios(
    shouldFetchCreator ? userId : null
  );
  
  // Refetch when navigating to booking-related pages
  useEffect(() => {
    if (location.pathname.includes('/bookings') || location.pathname.includes('/calendar')) {
      // Small delay to ensure booking was marked as viewed first
      const timer = setTimeout(() => {
        if (shouldFetchExplorer && refetchExplorer) refetchExplorer();
        if (shouldFetchCreator && refetchCreator) refetchCreator();
        setRefreshTrigger(prev => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, shouldFetchExplorer, shouldFetchCreator, refetchExplorer, refetchCreator]);
  
  // Listen for storage changes (when bookings are marked as viewed)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('bookingViewedStatuses_')) {
        setRefreshTrigger(prev => prev + 1);
        if (shouldFetchExplorer && refetchExplorer) refetchExplorer();
        if (shouldFetchCreator && refetchCreator) refetchCreator();
      }
    };
    
    const handleBookingViewed = () => {
      setRefreshTrigger(prev => prev + 1);
      if (shouldFetchExplorer && refetchExplorer) refetchExplorer();
      if (shouldFetchCreator && refetchCreator) refetchCreator();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('bookingViewed', handleBookingViewed);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('bookingViewed', handleBookingViewed);
    };
  }, [shouldFetchExplorer, shouldFetchCreator, refetchExplorer, refetchCreator]);
  
  const notificationCount = useMemo(() => {
    if (!userId) return 0;
    
    if (mode === 'explorer') {
      if (explorerLoading || !explorerBookings?.items) {
        return 0;
      }
      
      // Count bookings with unviewed status changes
      // IMPORTANT: For Entdecker (guests), don't show notifications for:
      // - PENDING bookings they just created
      // - CANCELLED bookings they cancelled themselves
      // Only show when host responds (status changes from PENDING) or host cancels
      const unviewedBookings = explorerBookings.items.filter(booking => {
        if (!booking || !booking.id || !booking.status) {
          return false;
        }
        
        // Don't show notification for PENDING bookings in explorer mode
        // The user just created them, so they don't need a notification
        // Only show when status changes FROM PENDING to something else (CONFIRMED, DECLINED, etc.)
        if (booking.status === 'PENDING') {
          // Check if this booking was previously viewed with a different status
          // If it was never viewed or was viewed as PENDING, don't show notification
          const lastViewed = getLastViewedStatus(booking.id, userId);
          if (!lastViewed || lastViewed.status === 'PENDING') {
            return false; // User just created this booking, no notification needed
          }
          // If last viewed status was different, show notification (status changed back to PENDING - rare case)
          return true;
        }
        
        // Don't show notification for CANCELLED bookings if the user cancelled it themselves
        if (booking.status === 'CANCELLED' && booking.cancelledBy === 'GUEST') {
          return false; // User cancelled it themselves, no notification needed
        }
        
        // For non-PENDING statuses (CONFIRMED, DECLINED, CANCELLED by host, etc.), 
        // show notification if status has changed since last view
        const hasUnviewed = hasUnviewedStatusChange(booking, userId);
        if (!hasUnviewed) {
          return false;
        }
        
        // Check if expired (24h rule for status changes)
        if (booking.createdAt) {
          const created = new Date(booking.createdAt);
          const now = new Date();
          const hoursPassed = (now - created) / (1000 * 60 * 60);
          const within24h = hoursPassed <= 24;
          
          if (!within24h) {
            return false;
          }
          
        }
        
        return true;
      });
      
      return unviewedBookings.length;
    } else if (mode === 'creator') {
      // Creator mode
      if (creatorLoading || !creatorBookings?.items) {
        return 0;
      }
      
      // Count ONLY NEW bookings (never viewed + PENDING status)
      // NOT status changes - only show notification for brand new bookings
      const newBookings = creatorBookings.items.filter(booking => {
        if (!booking || !booking.id || !booking.status) {
          return false;
        }
        
        // Only count NEW bookings (never viewed + PENDING)
        const isNewBooking = hasNewBooking(booking, userId);
        if (!isNewBooking) {
          return false;
        }
        
        // For PENDING bookings, check if expired (24h rule)
        if (booking.createdAt) {
          const created = new Date(booking.createdAt);
          const now = new Date();
          const hoursPassed = (now - created) / (1000 * 60 * 60);
          const within24h = hoursPassed <= 24;
          
          if (!within24h) {
            return false;
          }
          
        }
        
        return true;
      });
      
      return newBookings.length;
    } else {
      // Mode is null
      return 0;
    }
  }, [userId, mode, explorerBookings, creatorBookings, explorerLoading, creatorLoading, refreshTrigger]);
  
  return notificationCount;
};

export default useBookingNotifications;

